/**
 * Android hardware back-button behaviour ("double back to exit").
 *
 * Standard Android UX: on the app's root screen, one back press doesn't quit —
 * it shows a bottom snackbar hint and arms a short window; a second press within
 * that window exits. Anywhere else the back button just navigates one screen
 * back, exactly as before.
 *
 * How "root" is detected: Capacitor's `backButton` event carries `canGoBack`,
 * which reflects the WebView history stack. Because the app uses
 * `createBrowserRouter` (History API navigation), that stack tracks in-app
 * navigation faithfully — `canGoBack === false` means there is nothing left to
 * pop, i.e. we're at the root.
 *
 * Adding a `backButton` listener disables Capacitor's default handling, so we
 * drive both branches ourselves: `window.history.back()` for normal navigation
 * (React Router picks up the popstate) and `App.exitApp()` to quit.
 *
 * Mounted only inside the accepted branch of PrivacyConsentGate, so it never
 * runs alongside the consent screen's own back-button listener. Renders the
 * snackbar itself (a fixed, bottom-centred Material-style pill).
 */
import { useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { runTopBackHandler } from "@/hooks/useBackHandler";
import { cn } from "@/lib/utils";

const EXIT_WINDOW_MS = 2000;

export function AndroidBackButton() {
  // Snackbar visibility. Because a single component owns it, only one snackbar
  // can ever be on screen — re-arming just keeps it shown, never stacks.
  const [open, setOpen] = useState(false);
  // Whether we're within the "press again to exit" window, and the timer that
  // closes it. Refs (not state) so the listener sees live values without being
  // re-registered on every change.
  const armedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Hardware back is Android-only; iOS/web have no such button.
    if (Capacitor.getPlatform() !== "android") return;

    const disarm = () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      armedRef.current = false;
      setOpen(false);
    };

    const sub = CapacitorApp.addListener("backButton", ({ canGoBack }) => {
      // An open overlay (attachment image/PDF viewer, lightbox, …) claims Back
      // FIRST: close it instead of navigating away or arming exit. Without this
      // the back gesture pops the route and leaves the inspection screen.
      if (runTopBackHandler()) {
        disarm();
        return;
      }

      // Not at the root: navigate back one screen and cancel any pending exit
      // window. Existing navigation is untouched.
      if (canGoBack) {
        disarm();
        window.history.back();
        return;
      }

      // At the root, second press within the window → quit.
      if (armedRef.current) {
        disarm();
        void CapacitorApp.exitApp();
        return;
      }

      // At the root, first press → show the snackbar + arm the window.
      armedRef.current = true;
      setOpen(true);
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        armedRef.current = false;
        timerRef.current = null;
        setOpen(false);
      }, EXIT_WINDOW_MS);
    });

    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = null;
      armedRef.current = false;
      void sub.then((handle) => handle.remove());
    };
  }, []);

  if (!open) return null;

  return (
    <div
      // Fixed, bottom-centred; clears the Android nav-bar / gesture inset. The
      // pill never intercepts touches, so nothing behind it is blocked.
      className="pointer-events-none fixed inset-x-0 z-50 flex justify-center px-4"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 1.5rem)" }}
      role="status"
      aria-live="polite"
    >
      <div
        className={cn(
          // Inverse surface = the classic Material snackbar look, and it reads
          // correctly in both light and dark themes.
          "max-w-[90vw] rounded-lg bg-fg px-4 py-3 text-sm font-medium text-bg shadow-lg",
          "animate-in fade-in slide-in-from-bottom-4 duration-[var(--dur-fast)] ease-[var(--ease-out)]",
        )}
      >
        Press back again to exit.
      </div>
    </div>
  );
}
